import { Link, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Settings,
  CreditCard,
  LogOut,
  ChevronDown,
} from 'lucide-react';
import { supabase } from '@/lib/supabase';
import Loading from '@/components/loading';
import { useProfile } from '@/lib/providers';
import { splitNames } from '@/lib/utils';
import { AdminRole } from '@/types/custom';
import { LOGIN_PATH } from '@/lib/constants';

export function ProfileButton() {
  const location = useLocation();
  const { profile, loadingProfile } = useProfile();

  // Don't render on login, reset password, meditation detail, or binaural beat pages
  const isLoginPage =
    location.pathname === '/login' ||
    location.pathname === '/hoofdhartlogin' ||
    location.pathname === '/reset-password' ||
    location.pathname === '/admin/login' ||
    location.pathname === '/admin/reset-password-confirm';

  // Check if current page is a meditation detail page
  const isMeditationPage = location.pathname.match(/^\/meditaties\/[\w-]+$/);

  // Check if current page is a binaural beat detail page
  const isBinauralBeatPage = location.pathname.match(
    /^\/binaural-beat\/[\w-]+$/,
  );

  if (isLoginPage || isMeditationPage || isBinauralBeatPage) {
    return null;
  }

  if (loadingProfile) return <Loading />;
  if (!profile) return null;

  if (profile.role === AdminRole) return null;

  const [firstName, lastName] = splitNames(profile.naam ?? '');
  return (
    <div className="fixed top-4 right-4 lg:top-12 lg:right-12 z-50 cap-top cap-hide-on-play">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className="rounded-full px-5 py-2.5 bg-green-600 hover:bg-green-700 text-white font-medium flex items-center gap-2 text-base hhu-profile-btn"
          >
            <span>{firstName}</span>
            <ChevronDown className="h-4 w-4 opacity-70" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-[18.67rem]">
          {/* 56px * 1.33 = ~74.5px = 18.67rem */}
          <DropdownMenuLabel>
            <div className="flex items-center gap-3">
              <div
                className="h-10 w-10 rounded-full overflow-hidden"
                style={{ borderRadius: '50%', aspectRatio: '1/1' }}
              >
                <img
                  src={profile.role === 'hoofdhartuser' ? '/images/hoofdnaarhart-icon.png' : '/logo_white.png'}
                  alt="Logo"
                  className="h-full w-full object-cover"
                  style={{ borderRadius: '50%' }}
                />
              </div>
              <div className="flex flex-col space-y-1">
                <p className="text-sm font-medium">{firstName || 'Leo'}</p>
                <p className="text-xs text-muted-foreground truncate">
                  {profile?.email || ''}
                </p>
              </div>
            </div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem className="py-2">
            <Link to="/profiel" className="flex items-center w-full">
              <Settings className="mr-2 h-4 w-4" />
              <span>Profiel instellingen</span>
            </Link>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={async () => {
              await supabase.auth.signOut();
              window.location.href = LOGIN_PATH;
            }}
            className="text-red-600 hover:text-red-700 hover:bg-red-50 cursor-pointer py-2"
          >
            <LogOut className="mr-2 h-4 w-4" />
            <span>Uitloggen</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
